import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api, SERVICES } from "../../lib/api";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";

const SYSTEM_STATUS = [
  "New system required",
  "Existing system",
  "Existing system with a fault",
  "Existing system requiring maintenance",
  "Not sure",
];

const CONTACT_METHODS = ["Email", "Phone", "Either"];

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  company: z.string().optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  email: z.string().email("Please enter a valid email address"),
  site_location: z.string().optional().or(z.literal("")),
  number_of_sites: z.string().optional().or(z.literal("")),
  existing_system: z.string().optional().or(z.literal("")),
  preferred_contact: z.string().optional().or(z.literal("")),
  service: z.string().min(1, "Please select a service"),
  message: z.string().min(10, "Please share a few details (10+ characters)"),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please tick the box to submit your enquiry" }),
  }),
});

export default function QuoteForm({ defaultService = "", defaultSystem = "" }) {
  const [submitted, setSubmitted] = useState(false);

  const defaults = {
    name: "",
    company: "",
    phone: "",
    email: "",
    site_location: "",
    number_of_sites: "",
    existing_system: defaultSystem || "",
    preferred_contact: "",
    service: defaultService || "",
    message: "",
    consent: false,
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const service = watch("service");
  const existing_system = watch("existing_system");
  const preferred_contact = watch("preferred_contact");
  const consent = watch("consent");

  const onSubmit = async (values) => {
    try {
      await api.post("/leads", values);
      toast.success("Thank you — your enquiry has been received.");
      setSubmitted(true);
      reset(defaults);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    }
  };

  if (submitted) {
    return (
      <div className="fss-card border-green-700/30 bg-green-50/50" data-testid="quote-form-success">
        <div className="flex items-start gap-4">
          <CheckCircle2 className="w-8 h-8 text-green-700 shrink-0" />
          <div>
            <h3 className="font-display text-xl font-semibold text-slate-900">
              Thank you for contacting Fire Safety Systems.
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Your enquiry has been received and a member of our team will be in touch.
              For urgent matters please call{" "}
              <a className="text-green-700 font-semibold" href="tel:018351454">
                01 835 1454
              </a>
              .
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-5 fss-btn fss-btn-outline"
              data-testid="quote-form-reset"
            >
              Submit another enquiry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="fss-card"
      data-testid="quote-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Name" error={errors.name?.message} required>
          <input {...register("name")} className="fss-input" placeholder="Your full name" data-testid="quote-input-name" autoComplete="name" />
        </Field>
        <Field label="Company" error={errors.company?.message}>
          <input {...register("company")} className="fss-input" placeholder="Your company name" data-testid="quote-input-company" autoComplete="organization" />
        </Field>
        <Field label="Email" error={errors.email?.message} required>
          <input {...register("email")} type="email" className="fss-input" placeholder="you@company.ie" data-testid="quote-input-email" autoComplete="email" />
        </Field>
        <Field label="Telephone" error={errors.phone?.message}>
          <input {...register("phone")} className="fss-input" placeholder="01 835 1454" data-testid="quote-input-phone" autoComplete="tel" />
        </Field>
        <Field label="Site Location" error={errors.site_location?.message}>
          <input {...register("site_location")} className="fss-input" placeholder="e.g. Dublin 2, or full site address" data-testid="quote-input-site-location" />
        </Field>
        <Field label="Number of Sites" error={errors.number_of_sites?.message}>
          <input {...register("number_of_sites")} className="fss-input" placeholder="e.g. 1, or 3 sites" data-testid="quote-input-number-of-sites" />
        </Field>

        <div className="md:col-span-2">
          <Field label="Service Required" error={errors.service?.message} required>
            <Select value={service} onValueChange={(v) => setValue("service", v, { shouldValidate: true })}>
              <SelectTrigger className="h-12 rounded-sm border-slate-200 focus:ring-green-700" data-testid="quote-select-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    data-testid={`quote-service-option-${s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  >
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="New or Existing System" error={errors.existing_system?.message}>
          <Select value={existing_system} onValueChange={(v) => setValue("existing_system", v, { shouldValidate: true })}>
            <SelectTrigger className="h-12 rounded-sm border-slate-200 focus:ring-green-700" data-testid="quote-select-system">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {SYSTEM_STATUS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`system-option-${s.split(" ")[0].toLowerCase()}`}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Preferred Contact Method" error={errors.preferred_contact?.message}>
          <Select value={preferred_contact} onValueChange={(v) => setValue("preferred_contact", v, { shouldValidate: true })}>
            <SelectTrigger className="h-12 rounded-sm border-slate-200 focus:ring-green-700" data-testid="quote-select-contact">
              <SelectValue placeholder="Email, Phone or Either" />
            </SelectTrigger>
            <SelectContent>
              {CONTACT_METHODS.map((c) => (
                <SelectItem key={c} value={c} data-testid={`contact-option-${c.toLowerCase()}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="md:col-span-2">
          <Field label="Message" error={errors.message?.message} required>
            <textarea {...register("message")} rows={5} className="fss-input fss-textarea" placeholder="Please share a few details about your premises, existing systems and what you require..." data-testid="quote-input-message" />
          </Field>
        </div>
      </div>

      {/* Consent */}
      <div className="mt-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            id="consent"
            checked={!!consent}
            onCheckedChange={(v) => setValue("consent", !!v, { shouldValidate: true })}
            className="mt-0.5 border-slate-300 data-[state=checked]:bg-green-700 data-[state=checked]:border-green-700"
            data-testid="quote-consent"
          />
          <span className="text-sm text-slate-600 leading-relaxed">
            I agree to be contacted by Fire Safety Systems regarding my enquiry.
            See our{" "}
            <a href="/privacy" className="text-green-700 font-semibold hover:underline">
              Privacy Policy
            </a>
            .
          </span>
        </label>
        {errors.consent?.message && (
          <div className="mt-1 text-xs text-red-600 font-medium">{errors.consent.message}</div>
        )}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button type="submit" disabled={isSubmitting} className="fss-btn fss-btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed" data-testid="quote-submit">
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Enquiry
            </>
          )}
        </button>
        <p className="text-xs text-slate-500">
          Prefer to call? Ring{" "}
          <a href="tel:018351454" className="text-slate-700 font-semibold">
            01 835 1454
          </a>
          .
        </p>
      </div>
    </form>
  );
}

function Field({ label, error, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold tracking-[0.18em] uppercase text-slate-700 mb-2">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      {children}
      {error && (
        <span className="block mt-1 text-xs text-red-600 font-medium">{error}</span>
      )}
    </label>
  );
}
